import axios from "axios";
import {
  type BasePerformanceRequestFilterParams,
  type UserPerformanceRequestFilterWithPeriodParams,
  type PaginationParams,
} from "features/Library/Common/models";
import {
  type PeopleOverviewAccountsItem,
  type PeopleActivityDetails,
  type PeopleInteractionItem,
  type PeopleInteractionStatistics,
  type PeopleLineChart,
  type PeopleOutreachItem,
  type PeopleOutreachStatistics,
  type PeopleOverviewLineChart,
  type PeopleStatistics,
  type UsersLineChart,
  type UsersTable,
  type PeopleOverviewUsersItem,
  type PeopleOverviewStatistics,
  type AcquisitionLineChart,
  type AcquisitionEngagementChart,
  type AcquisitionEngagementCards,
  type AcquisitionCampaign,
  type PeopleAcquisitionDetails,
} from "../types/people";
import { processPerformanceParams } from "utils/queryUtils";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";

// Overview report
export const OVERVIEW_LINE_CHART_ENDPOINT = "/api/reports/v4/users/overview/linechart";
export const OVERVIEW_STATISTICS_ENDPOINT = "/api/reports/v4/users/overview/engagement";
export const OVERVIEW_ACCOUNTS_TABLE_ENDPOINT = "/api/reports/v4/users/overview/accounts";
export const OVERVIEW_USERS_TABLE_ENDPOINT = "/api/reports/v4/users/overview/users";
export const OVERVIEW_USERS_TABLE_EXPORT_ENDPOINT = "/api/dataexport/v4/users/overview/users";
export const OVERVIEW_ACCOUNTS_TABLE_EXPORT_ENDPOINT = "/api/dataexport/v4/users/overview/accounts";

// Activity report
export const ACTIVITY_LINE_CHART_ENDPOINT = "/api/reports/v3/users/activity/linechart";
export const INTERACTION_STATISTICS_ENDPOINT = "/api/reports/v3/users/interaction/cards";
export const INTERACTION_TABLE_ENDPOINT = "/api/reports/v3/users/interaction/table";
export const OUTREACH_STATISTICS_ENDPOINT = "/api/reports/v3/users/outreach/cards";
export const OUTREACH_TABLE_ENDPOINT = "/api/reports/v3/users/outreach/table";
export const ACTIVITY_DETAILS_ENDPOINT = "/api/reports/v2/users/activity/details";
export const OUTREACH_DETAILS_ENDPOINT = "/api/reports/v3/users/outreach/details";
export const INTERACTION_DETAILS_ENDPOINT = "/api/reports/v3/users/interaction/details";
export const ACTIVITY_EXPORT_ENDPOINT = "/api/dataexport/v3/users/activity/all/interaction/details";
export const OUTREACH_DETAILS_EXPORT_ENDPOINT = "/api/dataexport/v3/users/outreach/details";
export const INTERACTION_DETAILS_EXPORT_ENDPOINT = "/api/dataexport/v3/users/interaction/details";

// Acquisition report
export const ACQUISITION_LINE_CHART_ENDPOINT = "/api/reports/v1/acquisition/linechart";
export const ACQUISITION_ENGAGEMENT_CHART_ENDPOINT = "/api/reports/v1/acquisition/engagement-chart";
export const ACQUISITION_ENGAGEMENT_CARDS_ENDPOINT = "/api/reports/v1/acquisition/engagement-cards";
export const ACQUISITION_CAMPAIGNS_ENDPOINT = "/api/reports/v2/overview/acquisition/campaigns";
export const ACQUISITION_EXPORT_ENDPOINT = "/api/dataexport/v1/overview/acquisition/performance";
export const ACQUISITION_DRILLDOWN_EXPORT_ENDPOINT = "/api/dataexport/v1/acquisition/users";
export const ACQUISITION_DETAILS_ENDPOINT = "/api/reports/v2/acquisition/users";

// Users report
export const USERS_LINE_CHART_ENDPOINT = "/api/reports/v3/users/linechart";
export const USERS_STATISTICS_ENDPOINT = "/api/reports/v3/users/cards";
export const USERS_TABLE_ENDPOINT = "/api/reports/v3/users/table";

export const USER_ACTIVITY_EXPORT_ENDPOINT = "/api/dataexport/v2/users/activity";

type DetailsFilterParams = BasePerformanceRequestFilterParams & { activityType: string };
type AcquisitionDetailsFilterParams = BasePerformanceRequestFilterParams & {
  campaign: string;
  medium: string;
  source: string;
};

export const peopleReportingService = {
  activity: {
    getLineChart: (params: UserPerformanceRequestFilterWithPeriodParams, signal: AbortSignal) =>
      axios.get<PeopleLineChart[]>(ACTIVITY_LINE_CHART_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getActivityExport: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get(ACTIVITY_EXPORT_ENDPOINT, {
        responseType: "blob",
        params: {
          ...params,
        },
        paramsSerializer: processPerformanceParams,
        signal,
      }),
    getUserActivityExport: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get(USER_ACTIVITY_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        paramsSerializer: processPerformanceParams,
        signal,
      }),
    getPeopleInteractionStatistics: (params: BasePerformanceRequestFilterParams) =>
      axios.get<PeopleInteractionStatistics>(INTERACTION_STATISTICS_ENDPOINT, {
        params,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleInteractionTable: (
      params: BasePerformanceRequestFilterParams,
      pagination?: PaginationParams,
      signal?: AbortSignal,
    ) =>
      axios.get<PeopleInteractionItem[]>(INTERACTION_TABLE_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleOutreachStatistics: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<PeopleOutreachStatistics>(OUTREACH_STATISTICS_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleOutreachTable: (
      params: BasePerformanceRequestFilterParams,
      pagination?: PaginationParams,
      signal?: AbortSignal,
    ) =>
      axios.get<PeopleOutreachItem[]>(OUTREACH_TABLE_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleOutreachDetails: (params: DetailsFilterParams, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<PeopleActivityDetails[]>(OUTREACH_DETAILS_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleInteractionDetails: (params: DetailsFilterParams, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<PeopleActivityDetails[]>(INTERACTION_DETAILS_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleInteractionExport: (params: DetailsFilterParams) =>
      axios.get(INTERACTION_DETAILS_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleOutreachExport: (params: DetailsFilterParams) =>
      axios.get(OUTREACH_DETAILS_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        paramsSerializer: processPerformanceParams,
      }),
  },
  acquisition: {
    getLineChart: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<AcquisitionLineChart[]>(ACQUISITION_LINE_CHART_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getEngagementChart: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<AcquisitionEngagementChart[]>(ACQUISITION_ENGAGEMENT_CHART_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getEngagementCards: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<AcquisitionEngagementCards>(ACQUISITION_ENGAGEMENT_CARDS_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getCampaigns: (params: BasePerformanceRequestFilterParams, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<AcquisitionCampaign[]>(ACQUISITION_CAMPAIGNS_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getAcquisitionExport: (params: BasePerformanceRequestFilterParams) =>
      axios.get(ACQUISITION_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        paramsSerializer: processPerformanceParams,
      }),
    getAcquisitionDrilldownExport: (params: AcquisitionDetailsFilterParams) =>
      axios.get(ACQUISITION_DRILLDOWN_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        paramsSerializer: processPerformanceParams,
      }),
    getPeopleAcquisitionDetails: (
      params: AcquisitionDetailsFilterParams,
      pagination?: PaginationParams,
      signal?: AbortSignal,
    ) =>
      axios.get<PeopleAcquisitionDetails[]>(ACQUISITION_DETAILS_ENDPOINT, {
        params: { ...params, ...pagination },
        paramsSerializer: processPerformanceParams,
        signal,
      }),
  },
  users: {
    getLineChart: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<UsersLineChart[]>(USERS_LINE_CHART_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getStatistics: (params: BasePerformanceRequestFilterParams, signal: AbortSignal) =>
      axios.get<PeopleStatistics>(USERS_STATISTICS_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getTable: (params: BasePerformanceRequestFilterParams, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<UsersTable[]>(USERS_TABLE_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
  },
  overview: {
    getLineChart: (params: PerformanceWithAccountFilter, signal: AbortSignal) =>
      axios.get<PeopleOverviewLineChart[]>(OVERVIEW_LINE_CHART_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getStatistics: (params: PerformanceWithAccountFilter, signal: AbortSignal) =>
      axios.get<PeopleOverviewStatistics>(OVERVIEW_STATISTICS_ENDPOINT, {
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getAccountsTable: (params: PerformanceWithAccountFilter, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<PeopleOverviewAccountsItem[]>(OVERVIEW_ACCOUNTS_TABLE_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getAccountsTableExport: (params: PerformanceWithAccountFilter, signal: AbortSignal) =>
      axios.get(OVERVIEW_ACCOUNTS_TABLE_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getUsersTable: (params: PerformanceWithAccountFilter, pagination?: PaginationParams, signal?: AbortSignal) =>
      axios.get<PeopleOverviewUsersItem[]>(OVERVIEW_USERS_TABLE_ENDPOINT, {
        params: { ...params, ...pagination },
        signal,
        paramsSerializer: processPerformanceParams,
      }),
    getUsersTableExport: (params: PerformanceWithAccountFilter, signal: AbortSignal) =>
      axios.get<PeopleOverviewUsersItem[]>(OVERVIEW_USERS_TABLE_EXPORT_ENDPOINT, {
        responseType: "blob",
        params,
        signal,
        paramsSerializer: processPerformanceParams,
      }),
  },
} as const;
